import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Notice,
  NoticePosition,
  NoticeStage,
  NoticeDocument,
  CreateNoticeRequest,
  NoticePositionResponse
} from '../models/notice.model';

@Injectable({
  providedIn: 'root'
})
export class NoticeService {
  private apiUrl = `${environment.apiUrl}/admin`;

  constructor(private http: HttpClient) {}

  /**
   * Step 1: Create basic notice information
   * POST /api/admin/editais/basic-info
   */
  createBasicInfo(data: CreateNoticeRequest): Observable<Notice> {
    const url = `${this.apiUrl}/editais/basic-info`;
    return this.http.post<Notice>(url, data);
  }

  /**
   * Step 1: Update basic notice information
   * PUT /api/admin/editais/{editalId}/basic-info
   */
  updateBasicInfo(editalId: number, data: CreateNoticeRequest): Observable<Notice> {
    const url = `${this.apiUrl}/editais/${editalId}/basic-info`;
    return this.http.put<Notice>(url, data);
  }

  /**
   * Step 2: Create positions for a notice
   * POST /api/admin/editais/{noticeId}/cargos
   */
  createPositions(noticeId: number, positions: NoticePositionResponse[]): Observable<NoticePosition[]> {
    return this.http.post<NoticePosition[]>(
      `${this.apiUrl}/editais/${noticeId}/cargos`,
      positions
    );
  }

  /**
   * Step 2: Update positions for a notice
   * PUT /api/admin/editais/{noticeId}/cargos
   */
  updatePositions(noticeId: number, positions: NoticePositionResponse[]): Observable<NoticePosition[]> {
    return this.http.put<NoticePosition[]>(
      `${this.apiUrl}/editais/${noticeId}/cargos`,
      positions
    );
  }

  /**
   * Step 5: Create tender stages (etapas)
   * POST /api/admin/editais/{editalId}/etapas
   */
  createStages(editalId: number, stages: NoticeStage[]): Observable<NoticeStage[]> {
    return this.http.post<NoticeStage[]>(
      `${this.apiUrl}/editais/${editalId}/etapas`,
      stages
    );
  }

  /**
   * Step 5: Update tender stages (etapas)
   * PUT /api/admin/editais/{editalId}/etapas
   */
  updateStages(editalId: number, stages: NoticeStage[]): Observable<NoticeStage[]> {
    return this.http.put<NoticeStage[]>(
      `${this.apiUrl}/editais/${editalId}/etapas`,
      stages
    );
  }

  /**
   * Step 3: Save syllabus/disciplines for a notice
   * POST /api/admin/editais/{editalId}/disciplinas
   */
  saveDisciplines(editalId: number, disciplines: any): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/editais/${editalId}/disciplinas`,
      disciplines
    );
  }

  /**
   * Step 4: Create exam cities for a notice
   * POST /api/admin/editais/{editalId}/cidades-realizacao
   */
  createExamCities(editalId: number, cities: Array<{ cidadeId: number }>): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/editais/${editalId}/cidades-realizacao`,
      cities
    );
  }

  /**
   * Step 4: Update exam cities for a notice
   * PUT /api/admin/editais/{editalId}/cidades-realizacao
   */
  updateExamCities(editalId: number, cities: Array<{ cidadeId: number }>): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/editais/${editalId}/cidades-realizacao`,
      cities
    );
  }

  /**
   * Get exam cities for a notice
   * GET /api/admin/editais/{editalId}/cidades-realizacao
   */
  getExamCities(editalId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.apiUrl}/editais/${editalId}/cidades-realizacao`
    );
  }

  /**
   * Step 6: Create tender documents
   * POST /api/admin/editais/{editalId}/documentos
   */
  createDocuments(editalId: number, documents: NoticeDocument[]): Observable<NoticeDocument[]> {
    return this.http.post<NoticeDocument[]>(
      `${this.apiUrl}/editais/${editalId}/documentos`,
      documents
    );
  }

  /**
   * Step 6: Update tender documents
   * PUT /api/admin/editais/{editalId}/documentos
   */
  updateDocuments(editalId: number, documents: NoticeDocument[]): Observable<NoticeDocument[]> {
    return this.http.put<NoticeDocument[]>(
      `${this.apiUrl}/editais/${editalId}/documentos`,
      documents
    );
  }

  /**
   * Upload documents with files using multipart/form-data
   * POST /api/admin/editais/{editalId}/documentos/upload-all
   */
  uploadDocuments(editalId: number, formData: FormData): Observable<NoticeDocument[]> {
    return this.http.post<NoticeDocument[]>(
      `${this.apiUrl}/editais/${editalId}/documentos/upload-all`,
      formData
    );
  }

  /**
   * Step 7: Publish notice
   * PUT /api/admin/editais/{editalId}/publish
   */
  publishNotice(editalId: number): Observable<any> {
    const url = `${this.apiUrl}/editais/${editalId}/publish`;
    // Send empty object as body - backend expects JSON
    return this.http.put<any>(url, {}, {
      observe: 'response' // Get full response to debug
    });
  }

  /**
   * Get notice by ID
   */
  getNoticeById(id: number): Observable<Notice> {
    return this.http.get<Notice>(`${this.apiUrl}/editais/${id}`);
  }

  /**
   * Get all editais
   */
  getNotices(filters?: {
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Observable<any> {
    let params = new HttpParams();

    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.search) {
      params = params.set('search', filters.search);
    }
    if (filters?.page) {
      params = params.set('page', filters.page.toString());
    }
    if (filters?.pageSize) {
      params = params.set('size', filters.pageSize.toString());
    }

    return this.http.get<any>(`${this.apiUrl}/editais`, { params });
  }

  /**
   * Get positions for a notice
   */
  getPositions(noticeId: number): Observable<NoticePosition[]> {
    return this.http.get<NoticePosition[]>(`${this.apiUrl}/editais/${noticeId}/cargos`);
  }

  /**
   * Get modalidades (educational levels) for a notice
   * GET /api/admin/editais/{editalId}/modalidades
   */
  getModalidades(editalId: number): Observable<Array<{ codigo: string; descricao: string }>> {
    return this.http.get<Array<{ codigo: string; descricao: string }>>(
      `${this.apiUrl}/editais/${editalId}/modalidades`
    );
  }

  /**
   * Get stages for a notice/edital
   */
  getStages(editalId: number): Observable<NoticeStage[]> {
    return this.http.get<NoticeStage[]>(`${this.apiUrl}/editais/${editalId}/etapas`);
  }

  /**
   * Get documents for a notice/edital
   */
  getDocuments(editalId: number): Observable<NoticeDocument[]> {
    return this.http.get<NoticeDocument[]>(`${this.apiUrl}/editais/${editalId}/documentos`);
  }

  /**
   * Delete a notice
   */
  deleteNotice(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/editais/${id}`);
  }

  /**
   * Delete a position
   */
  deletePosition(noticeId: number, positionId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/editais/${noticeId}/positions/${positionId}`);
  }

  /**
   * Delete a stage
   */
  deleteStage(editalId: number, stageId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/editais/${editalId}/etapas/${stageId}`);
  }

  /**
   * Delete a document
   */
  deleteDocument(editalId: number, documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/editais/${editalId}/documentos/${documentId}`);
  }

  /**
   * Replace all documents (substitui todos os documentos)
   * POST /api/admin/editais/{editalId}/documentos
   * @param editalId - Notice ID
   * @param formData - FormData with files and document types
   */
  replaceDocuments(editalId: number, formData: FormData): Observable<NoticeDocument[]> {
    return this.http.post<NoticeDocument[]>(
      `${this.apiUrl}/editais/${editalId}/documentos`,
      formData
    );
  }

  /**
   * Add a document without replacing existing ones (adiciona um documento sem substituir os existentes)
   * POST /api/admin/editais/{editalId}/documentos/adicionar
   * @param editalId - Notice ID
   * @param formData - FormData with file and document type
   */
  addDocument(editalId: number, formData: FormData): Observable<NoticeDocument> {
    return this.http.post<NoticeDocument>(
      `${this.apiUrl}/editais/${editalId}/documentos/adicionar`,
      formData
    );
  }

  /**
   * Upload or replace photo for a notice
   * POST /api/admin/editais/{editalId}/foto
   * @param editalId - Notice ID
   * @param file - Photo file
   */
  uploadFoto(editalId: number, file: File): Observable<Notice> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Notice>(
      `${this.apiUrl}/editais/${editalId}/foto`,
      formData
    );
  }

  /**
   * Delete photo for a notice
   * DELETE /api/admin/editais/{editalId}/foto
   * @param editalId - Notice ID
   */
  deleteFoto(editalId: number): Observable<Notice> {
    return this.http.delete<Notice>(
      `${this.apiUrl}/editais/${editalId}/foto`
    );
  }

  /**
   * List all enrollments for a notice
   * GET /api/admin/editais/{editalId}/inscricoes
   */
  getEnrollments(
    editalId: number,
    page: number = 0,
    size: number = 10,
    search?: string,
    sortBy: string = 'createdAt',
    sortDirection: string = 'DESC'
  ): Observable<PageResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PageResponse<any>>(
      `${this.apiUrl}/editais/${editalId}/inscricoes`,
      { params }
    );
  }

  /**
   * List confirmed enrollments for a notice
   * GET /api/admin/editais/{editalId}/inscricoes/confirmadas
   */
  getConfirmedEnrollments(
    editalId: number,
    page: number = 0,
    size: number = 10,
    search?: string,
    sortBy: string = 'createdAt',
    sortDirection: string = 'DESC'
  ): Observable<PageResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PageResponse<any>>(
      `${this.apiUrl}/editais/${editalId}/inscricoes/confirmadas`,
      { params }
    );
  }

  /**
   * List pending enrollments for a notice
   * GET /api/admin/editais/{editalId}/inscricoes/pendentes
   */
  getPendingEnrollments(
    editalId: number,
    page: number = 0,
    size: number = 10,
    search?: string,
    sortBy: string = 'createdAt',
    sortDirection: string = 'DESC'
  ): Observable<PageResponse<any>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PageResponse<any>>(
      `${this.apiUrl}/editais/${editalId}/inscricoes/pendentes`,
      { params }
    );
  }

  /**
   * Export enrollments for a notice to spreadsheet
   * GET /api/admin/editais/{editalId}/inscricoes/exportar
   * @param editalId - Notice ID
   * @param status - Optional filter by status (INSCRITOS or CONFIRMADOS)
   * @param cota - Optional filter by quota
   */
  exportEnrollments(
    editalId: number,
    status?: string,
    cota?: string
  ): Observable<{ inscricoes: Array<{
    nomeCandidato: string;
    numeroInscricao: string;
    statusPagamento: string;
    cargoEscolhido: string;
    vaga: string;
  }> }> {
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }
    if (cota) {
      params = params.set('cota', cota);
    }

    return this.http.get<{ inscricoes: Array<{
      nomeCandidato: string;
      numeroInscricao: string;
      statusPagamento: string;
      cargoEscolhido: string;
      vaga: string;
    }> }>(
      `${this.apiUrl}/editais/${editalId}/inscricoes/exportar`,
      { params }
    );
  }
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
}
